import React, { useState, useEffect } from 'react';
import { login, setServerUrl, getServerUrl } from '../utils/api';

export default function LoginPage({ onLogin, manualLogout }) {
  const [form, setForm] = useState({ usuario: '', password: '' });
  const [recordar, setRecordar] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [autoLoginFailed, setAutoLoginFailed] = useState(false);
  const [serverInput, setServerInput] = useState('');
  const [serverGuardado, setServerGuardado] = useState(false);

  useEffect(() => {
    const init = async () => {
      // Cargar la URL del servidor guardada
      let url = getServerUrl();
      if (window.electronAPI) {
        url = await window.electronAPI.getServerUrl();
        // Asegurarse de que api.js también la tenga actualizada
        await setServerUrl(url);
      }
      setServerInput(url);

      if (manualLogout) return;

      if (window.electronAPI) {
        const creds = await window.electronAPI.getCredentials();
        if (creds.usuario && creds.password) {
          setForm({ usuario: creds.usuario, password: creds.password });
          setRecordar(true);
          try {
            setLoading(true);
            const data = await login(creds.usuario, creds.password);
            onLogin(data.usuario, data.token);
          } catch (err) {
            // Si no hay conexión pero hay token guardado, entrar en modo offline
            const savedToken = localStorage.getItem('token');
            const savedUser = localStorage.getItem('user');
            if (savedToken && savedUser) {
              onLogin(JSON.parse(savedUser), savedToken);
            } else {
              setAutoLoginFailed(true);
              setLoading(false);
            }
          }
        }
      }
    };
    init();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const data = await login(form.usuario, form.password);
      if (window.electronAPI) {
        if (recordar) {
          await window.electronAPI.saveCredentials({ usuario: form.usuario, password: form.password });
        } else {
          await window.electronAPI.clearCredentials();
        }
      }
      onLogin(data.usuario, data.token);
    } catch (err) {
      setError(err.response?.data?.error || 'No se pudo conectar al servidor. Verifique la IP del servidor.');
    } finally {
      setLoading(false);
    }
  };

  const saveServer = async () => {
    const url = serverInput.trim().replace(/\/$/, ''); // quitar barra final si la hay
    await setServerUrl(url);
    setServerInput(url);
    setServerGuardado(true);
    setError('');
    setTimeout(() => setServerGuardado(false), 2500);
  };

  // Permitir guardar con Enter en el campo de servidor
  const handleServerKeyDown = (e) => {
    if (e.key === 'Enter') { e.preventDefault(); saveServer(); }
  };

  if (loading && !autoLoginFailed) {
    return (
      <div className="login-page" style={{ justifyContent: 'center' }}>
        <div style={{ textAlign: 'center', color: 'white' }}>
          <div className="login-emblem" style={{ margin: '0 auto 20px' }}>AL</div>
          <h2 style={{ fontWeight: 500, marginBottom: 8 }}>Asamblea Legislativa Departamental de Potosí</h2>
          <p style={{ opacity: 0.5, fontSize: 13 }}>Iniciando sesión automáticamente...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="login-page">
      <div className="login-left">
        <div className="login-emblem">AL</div>
        <h1>Asamblea Legislativa<br/>Departamental de Potosí</h1>
        <p>Sistema de Gestión Documental<br/>Sesiones y Tratamiento</p>
      </div>
      <div className="login-right">
        <h2>Iniciar sesión</h2>
        <p>
          {autoLoginFailed
            ? 'Error de conexión. Ingrese sus datos manualmente.'
            : manualLogout
            ? 'Sesión cerrada. Ingrese sus credenciales para volver a entrar.'
            : 'Ingrese sus credenciales para acceder'}
        </p>

        {error && <div className="alert alert-err">{error}</div>}
        {autoLoginFailed && <div className="alert alert-err">No se pudo iniciar sesión automáticamente. Verifique la IP del servidor.</div>}

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Usuario</label>
            <input
              type="text"
              placeholder="Nombre de usuario"
              value={form.usuario}
              onChange={e => setForm(f => ({ ...f, usuario: e.target.value }))}
              autoFocus={!form.usuario}
              required
            />
          </div>
          <div className="form-group">
            <label>Contraseña</label>
            <input
              type="password"
              placeholder="••••••••"
              value={form.password}
              onChange={e => setForm(f => ({ ...f, password: e.target.value }))}
              required
            />
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
            <input
              type="checkbox"
              id="recordar"
              checked={recordar}
              onChange={e => setRecordar(e.target.checked)}
              style={{ width: 15, height: 15, cursor: 'pointer' }}
            />
            <label htmlFor="recordar" style={{ fontSize: 13, color: '#555', cursor: 'pointer', margin: 0 }}>
              Recordar y entrar automáticamente
            </label>
          </div>
          <button type="submit" className="btn-primary" disabled={loading}>
            {loading ? 'Verificando...' : 'Ingresar'}
          </button>
        </form>

        {window.electronAPI && (
          <div className="server-config">
            <label>
              Dirección del servidor (IP de red interna)
              {serverGuardado && (
                <span style={{ color: '#166534', marginLeft: 8, fontSize: 11, fontWeight: 500 }}>
                  ✓ Guardado
                </span>
              )}
            </label>
            <div className="server-config-row">
              <input
                value={serverInput}
                onChange={e => { setServerInput(e.target.value); setServerGuardado(false); }}
                onKeyDown={handleServerKeyDown}
                placeholder="https://personal.asambleadepotosi.gob.bo"
                style={{ borderColor: serverGuardado ? '#86efac' : undefined }}
              />
              <button
                onClick={saveServer}
                style={{ background: serverGuardado ? '#f0fdf4' : undefined, color: serverGuardado ? '#166534' : undefined }}
              >
                {serverGuardado ? '✓' : 'Guardar'}
              </button>
            </div>
            <p style={{ fontSize: 11, color: '#999', marginTop: 4 }}>
              Ejemplo: https://personal.asambleadepotosi.gob.bo — guarda primero antes de ingresar
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
